import { useState } from 'react';
import { Shell } from '../Shell';
import { useAsync } from '../../core/useAsync';
import { cachedLeague, getBoards, getMe } from '../../core/api';
import { avatarStyle, initials } from '../format';
import type { Boards, BoardEntry, LeagueInfo, Me } from '../../core/types';
import s from './Standings.module.css';

// The three boards on their own page, selectable. Points = what your entries
// scored; Guessing = submitters you named; Hiding = how many failed to spot you.
// Wired to GET /api/boards.

const BOARDS = {
  Points: { key: 'points', unit: 'pts', blurb: 'What your entries scored across the season, plus any calls that landed.' },
  Guessing: { key: 'guessing', unit: 'named', blurb: 'How many submitters you named correctly. The detective board.' },
  Hiding: { key: 'hiding', unit: 'fooled', blurb: 'How many players failed to spot you. High is good — you kept them guessing.' },
} as const;
type BoardName = keyof typeof BOARDS;
const NAMES = Object.keys(BOARDS) as BoardName[];
const podium = ['g', 's', 'b'];

export function Standings() {
  const league = useAsync<LeagueInfo>(cachedLeague, []);
  const seasonId = league.data?.season_id ?? null;
  const boards = useAsync<Boards>(() => (seasonId != null ? getBoards(seasonId) : Promise.resolve({ points: [], guessing: [], hiding: [] })), [seasonId]);
  const me = useAsync<Me>(getMe, []);
  const [board, setBoard] = useState<BoardName>('Points');

  const myId = me.data?.user?.user_id ?? null;
  const cfg = BOARDS[board];
  const rowsFor = (name: BoardName) =>
    [...((boards.data?.[BOARDS[name].key] as BoardEntry[]) ?? [])].sort((a, b) => b.total - a.total);

  return (
    <Shell>
      <div className={s.appbar}>
        <span className={s.ctx}>{league.data?.name ?? 'Standings'}</span>
        <button className={s.menu} aria-label="Menu">⋯</button>
      </div>
      <div className={s.head}><h2>Standings</h2></div>
      {/* Mobile: pick one board. Desktop: all three side by side (tabs hidden). */}
      <div className={s.tabs}>
        {NAMES.map((n) => (
          <button key={n} className={n === board ? s.on : ''} onClick={() => setBoard(n)}>{n}</button>
        ))}
      </div>
      <div className={s.blurb}>{cfg.blurb}</div>

      {boards.loading ? (
        <div className={s.blurb} style={{ border: 0, paddingTop: 20 }}>Loading…</div>
      ) : (
        <div className={s.boards}>
          {NAMES.map((n) => {
            const rows = rowsFor(n);
            return (
              <div key={n} className={`${s.boardcol}${n === board ? ' ' + s.activecol : ''}`}>
                <div className={s.colhead}>{n}<small>{BOARDS[n].unit}</small></div>
                {rows.length === 0 ? (
                  <div className={s.colempty}>No scores yet.</div>
                ) : (
                  <div className={s.rows}>
                    {rows.map((r, i) => (
                      <div key={r.user_id} className={`${s.brow}${r.user_id === myId ? ' ' + s.you : ''}`}>
                        <span className={`${s.rk}${i < 3 ? ' ' + s[podium[i]] : ''}`}>{i + 1}</span>
                        <span className={s.av} style={avatarStyle(r.avatar_url, r.user_id)}>{r.avatar_url ? '' : initials(r.display_name)}</span>
                        <span className={s.nm}>{r.display_name}</span>
                        <span className={s.val}><b>{r.total}</b><small>{BOARDS[n].unit}</small></span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Shell>
  );
}
